import React, { useRef, useEffect, useState, lazy, Suspense } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';

const EmailEditor = lazy(() => import('react-email-editor'));

const EditTemplateModal = ({
  isOpen,
  onClose,
  template,
  setTemplate,
  scenarios,            // ожидаю [{value, label}, ...]
  onSaveTemplate,
}) => {
  const emailEditorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);

  // грузим дизайн только когда редактор готов
  useEffect(() => {
    if (!isOpen) setEditorReady(false);
  }, [isOpen]);

  const handleSaveClick = async () => {
    const editor = emailEditorRef.current?.editor;
    if (!editorReady || !editor) return;

    editor.exportHtml(async ({ design, html }) => {
      // заменить unlayer CDN картинки на твой бэкенд
      const extractImageUrls = (htmlContent) => {
        const regex = /<img[^>]+src="(https:\/\/assets\.unlayer\.com\/[^"]+)"[^>]*>/g;
        const urls = [];
        let m;
        while ((m = regex.exec(htmlContent)) !== null) urls.push(m[1]);
        return urls;
      };

      const originalUrls = extractImageUrls(html);

      const uploadedUrls = await Promise.all(
        originalUrls.map(async (url) => {
          const res = await api.post('/api/uploads/from-url/', { url });
          return res.data?.url ?? null;
        }),
      );

      let updatedHtml = html;
      originalUrls.forEach((oldUrl, i) => {
        const nu = uploadedUrls[i];
        if (nu) updatedHtml = updatedHtml.replaceAll(oldUrl, nu);
      });

      onSaveTemplate({
        ...template,
        content: JSON.stringify(design),
        html: updatedHtml,
      });
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full !max-w-[1400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Template</DialogTitle>
          <DialogDescription>Modify the selected email template.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Template Name</Label>
            <Input
              value={template?.title || ''}
              onChange={(e) => setTemplate((p) => ({ ...p, title: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Scenario</Label>
              <Select
                value={template.scenario || ''}
                onValueChange={(value) => setTemplate((p) => ({ ...p, scenario: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a scenario" />
                </SelectTrigger>
                <SelectContent>
                  {(scenarios || []).map((s, i) => (
                    <SelectItem key={i} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tone</Label>
              <Select
                value={template.tone || ''}
                onValueChange={(value) => setTemplate((p) => ({ ...p, tone: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a tone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Email Step Number</Label>
              <Input
                type="number"
                min="1"
                value={template.step_number ?? 1}
                onChange={(e) => setTemplate((p) => ({ ...p, step_number: Number(e.target.value) }))}
              />
            </div>
          </div>

          <div>
            <Label>Subject</Label>
            <Input
              value={template.subject || ''}
              onChange={(e) => setTemplate((p) => ({ ...p, subject: e.target.value }))}
            />
          </div>

          <div>
            <Label>Email Body</Label>
            <div className="rounded border overflow-hidden" style={{ minHeight: 300 }}>
              {isOpen && (
                <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading editor…</div>}>
                  <EmailEditor
                    key={template?.id || 'new'}
                    ref={emailEditorRef}
                    onReady={() => {
                      setEditorReady(true);
                      const ed = emailEditorRef.current?.editor;
                      // подгрузим дизайн если есть
                      if (template?.content) {
                        try {
                          ed?.loadDesign(JSON.parse(template.content));
                        } catch {}
                      }
                      // перехват загрузки изображений
                      ed?.registerCallback('image', (file, done) => {
                        const f = file.attachments?.[0];
                        if (!f) return done({ progress: 100, url: '' });

                        const form = new FormData();
                        form.append('image', f);

                        api.post('/api/uploads/image/', form, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                        })
                          .then((res) => done({ progress: 100, url: res.data?.url || '' }))
                          .catch(() => done({ progress: 100, url: '' }));
                      });
                    }}
                    options={{
                      appearance: { theme: 'light' },
                      features: { imageEditor: false, stockImages: false },
                    }}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSaveClick} disabled={!editorReady}>Save Changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditTemplateModal;